import { MDXRemote, MDXRemoteSerializeResult } from 'next-mdx-remote';
import Head from 'next/head';
import Image from 'next/image';
import TicketBooker from '../../components/TicketBooker';
import TreeCollector from '../../components/TreeCollector';
import { getAllDocs, getDocBySlug } from '../../utils/static-content';

const components = {
  TicketBooker,
  TreeCollector,
  // eslint-disable-next-line react/display-name
  img: ({ src, alt }: { src: string; alt: string }) => {
    return (
      <Image alt={alt} src={require(`../../public/events/${src}`).default} placeholder="blur" />
    );
  }
};

export default function Page({
  source,
  frontMatter
}: {
  source: MDXRemoteSerializeResult;
  frontMatter: { title: string; date: string; time: string; address: string; heroImage: string };
}) {
  const getDate = (date: string | string[]) => {
    if (Array.isArray(date)) {
      const [start, end] = date;

      return `${new Date(start).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })} - ${new Date(end).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })}`;
    }

    return new Date(date).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <Head>
        <title>{frontMatter.title} | Events | York Round Table | #DoMore</title>
      </Head>
      <div className="mx-auto w-full max-w-screen-xl">
        <section className="mx-auto w-full max-w-screen-lg mb-16">
          <div className="mb-10 text-center">
            <h1 className="text-4xl text-rt-orange mb-4">{frontMatter.title}</h1>
            <ul className="text-white">
              {frontMatter.date ? (
                <li className="flex items-center justify-center mb-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 mr-2 opacity-40"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <span>
                    {getDate(frontMatter.date)}
                    {frontMatter.time ? ` @ ${frontMatter.time}` : null}
                  </span>
                </li>
              ) : null}
              {frontMatter.address ? (
                <li className="flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 mr-2 opacity-40"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span>
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        frontMatter.address
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="border-b-2 border-white border-opacity-25 hover:border-opacity-0 transition">
                      {frontMatter.address}
                      <span className="sr-only"> (opens in a new window)</span>
                    </a>
                  </span>
                </li>
              ) : null}
            </ul>
          </div>
        </section>

        {frontMatter.heroImage ? (
          <div className="w-full mb-10">
            <Image
              alt=""
              src={require(`../../public/events/${frontMatter.heroImage}`).default}
              placeholder="blur"
            />
          </div>
        ) : null}

        <div className="mx-auto w-full max-w-screen-md text-white mb-20">
          <MDXRemote {...source} components={components} />
        </div>
      </div>
    </>
  );
}

export async function getStaticPaths() {
  const docs = await getAllDocs('_events');

  return {
    fallback: false,
    paths: docs.map((doc) => ({ params: { slug: doc.frontMatter.slug } }))
  };
}

export async function getStaticProps({
  params
}: {
  params: { slug: string };
}): Promise<{ props: { source: unknown; frontMatter: Record<string, string> } } | undefined> {
  const doc = await getDocBySlug('_events', params.slug);

  if (doc) {
    const { compiledSource, frontMatter } = doc;
    return { props: { source: compiledSource, frontMatter } };
  }
}
